import XMarkIcon from '@heroicons/react/24/outline/XMarkIcon';
import { KeyboardEvent, ReactNode, useEffect, useId, useRef } from 'react';

import { ButtonPanel } from '@/components/core/ButtonPanel';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

/** Page padding that docks the panel from `xl`, matching its `md:tw-w-96`. */
const DAM_SIDE_PANEL_PAGE_PADDING = 'xl:tw-pr-96';

interface DamSidePanelProps {
    title: string;
    onClose: () => void;
    children: ReactNode;
}

/**
 * Panel on the right of the library (wireframe `_DesignOnly/Wireframe_Drawer`
 * `I6392:31622;6476:92549`, `I6392:31621;6476:92549`), below the 40 px app navbar.
 * From `xl` the page reserves its width (`DAM_SIDE_PANEL_PAGE_PADDING`) so it docks;
 * below `xl` it floats over the content, below `md` across the full width.
 * Non-modal; Escape closes it.
 */
function DamSidePanel({ title, onClose, children }: DamSidePanelProps) {
    const { t } = useTranslation();
    const titleId = useId();
    const panelRef = useRef<HTMLElement>(null);

    useEffect(() => {
        panelRef.current?.focus();
    }, []);

    const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === 'Escape') {
            event.stopPropagation();
            onClose();
        }
    };

    return (
        <aside
            ref={panelRef}
            tabIndex={-1}
            aria-labelledby={titleId}
            onKeyDown={handleKeyDown}
            className={cn(
                'tw-fixed tw-bottom-0 tw-right-0 tw-top-10 tw-z-30 tw-flex tw-w-full tw-flex-col tw-gap-4 tw-bg-fill-primary tw-p-4 tw-shadow-xl tw-outline-none',
                'md:tw-w-96 md:tw-border-y-0 md:tw-border-l md:tw-border-r-0 md:tw-border-solid md:tw-border-divider xl:tw-shadow-none',
            )}
        >
            <div className="tw-flex tw-items-center tw-gap-2">
                <h2 id={titleId} className="tw-m-0 tw-min-w-0 tw-flex-1 tw-text-t-base tw-font-bold tw-text-h-primary lg:tw-text-t-lg">
                    {title}
                </h2>
                <ButtonPanel
                    isDisabled={false}
                    icon={XMarkIcon}
                    aria-label={t('DAM.LIBRARY.CLOSE_PANEL')}
                    tooltip={t('DAM.LIBRARY.CLOSE_PANEL')}
                    onClick={onClose}
                />
            </div>
            <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto">
                {children}
            </div>
        </aside>
    );
}

export { DAM_SIDE_PANEL_PAGE_PADDING, DamSidePanel };
export type { DamSidePanelProps };
